import { useEffect, useState } from 'react';
import type { Conversation, TimelineIndex, TimelineMonthBucket, TimelineYearBucket } from '@unburden/common';
import { authFetch } from '../api';
import { ConversationList } from './ConversationList';

interface TimelineProps {
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
}

type ZoomLevel = 'years' | 'months' | 'conversations';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthLabel(month: string): string {
  const [, m] = month.split('-');
  return MONTH_NAMES[parseInt(m, 10) - 1];
}

export function Timeline({ selectedConversation, onSelectConversation }: TimelineProps) {
  const [index, setIndex] = useState<TimelineIndex | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [zoom, setZoom] = useState<ZoomLevel>('years');
  const [selectedYear, setSelectedYear] = useState<TimelineYearBucket | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<TimelineMonthBucket | null>(null);
  const [monthConversations, setMonthConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [failedConversationCount, setFailedConversationCount] = useState(0);

  const loadIndex = async () => {
    setLoading(true);
    setError(null);
    setMissing(false);
    try {
      const response = await authFetch('/api/timeline');
      if (response.status === 404) {
        setMissing(true);
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setIndex(await response.json());
    } catch {
      setError('Failed to load timeline.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndex();
  }, []);

  const handleBuild = async () => {
    setBuilding(true);
    setError(null);
    try {
      const response = await authFetch('/api/admin/timeline/build', { method: 'POST' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await loadIndex();
    } catch {
      setError('Failed to build timeline index.');
    } finally {
      setBuilding(false);
    }
  };

  const openYear = (year: TimelineYearBucket) => {
    setSelectedYear(year);
    setZoom('months');
  };

  // A single flaky fetch (transient network blip, cold edge, KV propagation
  // lag) shouldn't silently vanish a real conversation from the list — retry
  // once, and if it still fails, surface a count instead of hiding it.
  const fetchConversation = async (id: string): Promise<Conversation | null> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await authFetch(`/api/conversations/${encodeURIComponent(id)}`);
      if (response.ok) return response.json();
    }
    return null;
  };

  const openMonth = async (month: TimelineMonthBucket) => {
    setSelectedMonth(month);
    setZoom('conversations');
    setLoadingConversations(true);
    setFailedConversationCount(0);
    try {
      const results = await Promise.all(month.conversationIds.map(fetchConversation));
      const conversations = (results.filter(Boolean) as Conversation[]).sort(
        (a, b) => b.lastMessageTime - a.lastMessageTime
      );
      setMonthConversations(conversations);
      setFailedConversationCount(results.length - conversations.length);
    } catch {
      setMonthConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const backToYears = () => {
    setZoom('years');
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  const backToMonths = () => {
    setZoom('months');
    setSelectedMonth(null);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Loading timeline...</p>
      </div>
    );
  }

  if (missing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center gap-3">
        <p className="text-gray-500 text-sm">
          Timeline index hasn't been built yet.
        </p>
        <button
          onClick={handleBuild}
          disabled={building}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {building ? 'Building...' : 'Build timeline index'}
        </button>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (error || !index) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-red-500">{error || 'Timeline unavailable.'}</p>
      </div>
    );
  }

  if (index.years.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500">No messages indexed yet.</p>
      </div>
    );
  }

  if (zoom === 'years') {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 p-3">
        <div className="grid grid-cols-2 gap-2">
          {[...index.years].reverse().map(year => (
            <button
              key={year.year}
              onClick={() => openYear(year)}
              className="p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-left transition"
            >
              <p className="text-xl font-semibold text-gray-900">{year.year}</p>
              <p className="text-xs text-gray-500">
                {year.messageCount.toLocaleString()} messages
              </p>
              <p className="text-xs text-gray-500">
                {year.conversationIds.length} conversation{year.conversationIds.length === 1 ? '' : 's'}
              </p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (zoom === 'months' && selectedYear) {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 p-3">
        <button
          onClick={backToYears}
          className="text-xs text-blue-600 hover:underline mb-3"
        >
          &lsaquo; Years
        </button>
        <p className="text-sm font-semibold text-gray-900 mb-2">{selectedYear.year}</p>
        <div className="grid grid-cols-2 gap-2">
          {selectedYear.months.map(month => (
            <button
              key={month.month}
              onClick={() => openMonth(month)}
              className="p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-left transition"
            >
              <p className="text-sm font-medium text-gray-900">{monthLabel(month.month)}</p>
              <p className="text-xs text-gray-500">{month.messageCount.toLocaleString()} messages</p>
              <p className="text-xs text-gray-500">
                {month.conversationIds.length} conversation{month.conversationIds.length === 1 ? '' : 's'}
              </p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (zoom === 'conversations' && selectedYear && selectedMonth) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-3 pt-3 pb-1 flex items-center gap-2 text-xs">
          <button onClick={backToYears} className="text-blue-600 hover:underline">
            Years
          </button>
          <span className="text-gray-300">/</span>
          <button onClick={backToMonths} className="text-blue-600 hover:underline">
            {selectedYear.year}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600">{monthLabel(selectedMonth.month)}</span>
        </div>
        {failedConversationCount > 0 && (
          <p className="px-3 py-1.5 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
            {failedConversationCount} conversation{failedConversationCount === 1 ? '' : 's'} couldn't be
            loaded — reopen this month to retry.
          </p>
        )}
        <ConversationList
          conversations={monthConversations}
          selectedConversation={selectedConversation}
          onSelect={onSelectConversation}
          loading={loadingConversations}
        />
      </div>
    );
  }

  return null;
}
